import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import {
  ALL_MEMBERS,
  CREATE_PROJECT,
  DEFAULT_ICONS,
  DUE_DATE,
  PROJECT_LIST,
  STARRED_PROJECTS,
} from "services/endPoints";
import { privateRequest } from "services/privateRequest";
import { PATH } from "utils/pagePath";
import { getProjectId } from "utils/utils";

const initialState = {
  projectsList: [],
  membersList: [],
  selectCache: [],
  // pages currently being fetched for select infinite scroll (to prevent duplicates)
  selectFetchingPages: [],
  loading: false,
  loader: true,
  pageNo: 1,
  pageSize: 25,
  total: 0,
  selectTotal: 0,
  params: {},
  projectData: {},
  defaultIcons: [],
  sortByColumn: null,
  orderBy: null,
};

export const getProjectsList = createAsyncThunk(
  "entity/getProjectsList",
  async (params) => {
    const res = await privateRequest.get(PROJECT_LIST, { params });
    return {
      data: res?.data?.data?.data || [],
      total: res?.data?.data?.count?.total || 0,
      sortByColumn: params.sortByColumn || null,
      orderBy: params.orderBy || null,
    };
  },
  {
    // prevent duplicate select page fetches if already in-flight
    condition: (params, { getState }) => {
      try {
        const state = getState();
        if (params?.forSelect && params?.pageNo) {
          const inFlight = state.project?.selectFetchingPages || [];
          if (inFlight.includes(params.pageNo)) return false;
        }
      } catch (e) {
        // ignore
      }
      return true;
    },
  },
);

export const getProjectsMembersList = createAsyncThunk(
  "entity/getProjectsMembersList",
  async (params, { rejectWithValue }) => {
    try {
      const res = await privateRequest.get(ALL_MEMBERS, { params });

      return {
        data: res?.data?.data?.data || [],
        total: res?.data?.data?.count?.total || 0,
        sortByColumn: params.sortByColumn || null,
        orderBy: params.orderBy || null,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  },
);

export const toggleProjectStar = createAsyncThunk(
  "entity/toggleProjectStar",
  async ({ projectId, isStarred }, { rejectWithValue }) => {
    try {
      await privateRequest.post(`${STARRED_PROJECTS}/${projectId}`, {
        isStarred,
      });

      return { projectId, isStarred };
    } catch (err) {
      console.error("Backend error:", err.response?.data || err.message);
      return rejectWithValue(err.response?.data || "Unknown error");
    }
  },
);

export const getDefaultIcons = createAsyncThunk(
  "entity/getDefaultIcons",
  async () => {
    const res = await privateRequest.get(DEFAULT_ICONS);
    return {
      data: res?.data?.data || [],
    };
  },
);

export const getProjectData = createAsyncThunk(
  "entity/getProjectData",
  async (id) => {
    const res = await privateRequest.get(`${PROJECT_LIST}/${id}`);
    return {
      data: res?.data?.data || [],
    };
  },
);

export const createProject = createAsyncThunk(
  "entity/createProject",
  async (params) => {
    const formData = new FormData();
    formData.append("name", params.name);
    formData.append("key", params.key);
    formData.append("description", params.description);
    formData.append("startDate", params.startDate);
    formData.append("endDate", params.endDate);
    formData.append("templateType", params.templateType || "-");

    // if (params.requestType) {
    //   formData.append("requestType", params.requestType);
    // }

    const res = await privateRequest.post(CREATE_PROJECT, formData);
    if (res) {
      toast.success(res?.data?.message);
    }

    return {
      data: res?.data?.data || [],
      totalEntity: res?.data?.count || 0,
      message: res?.data?.message || "",
    };
  },
);

export const deleteProject = createAsyncThunk(
  "entity/deleteProject",
  async (params, { dispatch, getState }) => {
    const res = await privateRequest.delete(`${CREATE_PROJECT}/${params?.id}`);
    const state = getState?.().project;
    await dispatch(
      getProjectsList({
        pageNo: params?.pageNo,
        pageSize: state?.pageSize,
        sortByColumn: state?.sortByColumn,
        orderBy: state?.orderBy,
        params: state?.params,
      }),
    );
    toast.success(res?.data?.message);
    // return {
    //   data: res?.data?.data || [],
    //   totalEntity: res?.data?.count || 0,
    //   message: res?.data?.message || "",
    // };
  },
);

export const updateProject = createAsyncThunk(
  "entity/updateProject",
  async ({ formData, navigate, reset }) => {
    try {
      const res = await privateRequest.put(
        `${PROJECT_LIST}/${getProjectId()}`,
        formData,
      );
      if (res) {
        toast.success(res?.data?.message);
        navigate?.(PATH.PROJECTS);
        reset();
      }
    } catch (error) {
      console.log("Request canceled", error?.message);
      toast.error(error?.response?.data?.message);
    }
  },
);

export const dueDateExtensionRequest = createAsyncThunk(
  "entity/dueDateExtension",
  async (params) => {
    try {
      const res = await privateRequest.post(DUE_DATE, params);
      if (res) {
        toast.success(res?.data?.message);
      }
    } catch (error) {
      // toast.error(error?.response?.data?.message || "Something went wrong");
      console.error(error?.response?.data?.message || "Something went wrong");
    }
  },
);

const projectSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setLoader: (state, action) => {
      state.loader = action.payload;
    },
    setPageNo: (state, action) => {
      state.pageNo = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setSort: (state, action) => {
      state.sortByColumn = action.payload.sortByColumn;
      state.orderBy = action.payload.orderBy;
    },
    setParams: (state, action) => {
      state.params = action.payload;
    },
    clearProjectData: (state) => {
      state.projectData = initialState.projectData;
    },
    clearSelectCache: (state) => {
      state.selectCache = [];
      state.selectTotal = 0;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(toggleProjectStar.pending, (state, action) => {})
      .addCase(toggleProjectStar.fulfilled, (state, action) => {
        const { projectId, isStarred } = action.payload;
        state.projectsList = state.projectsList.map((proj) =>
          proj.id === projectId ? { ...proj, starred: isStarred } : proj,
        );
      })
      .addCase(toggleProjectStar.rejected, (state, action) => {
        console.error("toggleProjectStar.rejected:", action.error);
      });

    //project list reducers
    builder
      .addCase(getProjectsList.pending, (state, action) => {
        const isSelect = action?.meta?.arg?.forSelect;
        const page = action?.meta?.arg?.pageNo || 1;
        // only show main loader for table fetches, not select background fetches

        if (!isSelect) {
          state.loader = true;
          // ✅ Reset list only on fresh fetch so UI clears stale data immediately
          if (page === 1) {
            state.projectsList = [];
          }
        }

        if (!isSelect) state.loader = true;
        if (isSelect) {
          // mark this select page as in-flight
          if (!state.selectFetchingPages.includes(page))
            state.selectFetchingPages.push(page);
        }
      })
      .addCase(getProjectsList.fulfilled, (state, action) => {
        const isSelect = action?.meta?.arg?.forSelect;
        const newProjects = action.payload.data.map((project) => ({
          ...project,
          starred: project?.starred ?? project?.isStarred ?? false,
        }));

        if (isSelect) {
          // accumulate select cache (append pages) but don't touch main projectsList
          if ((action?.meta?.arg?.pageNo || 1) === 1) {
            state.selectCache = newProjects;
          } else {
            state.selectCache = [...state.selectCache, ...newProjects];
          }
          state.selectTotal = action.payload.total;
          // remove page from in-flight list
          const page = action?.meta?.arg?.pageNo || 1;
          state.selectFetchingPages = state.selectFetchingPages.filter(
            (p) => p !== page,
          );
        } else {
          if (state.pageNo === 1) {
            state.projectsList = newProjects;
          } else {
            state.projectsList = [...state.projectsList, ...newProjects];
          }
          state.total = action.payload.total;
          state.sortByColumn = action.payload.sortByColumn;
          state.orderBy = action.payload.orderBy;
        }
        // always clear loader flag for main table fetches
        if (!isSelect) state.loader = false;
      })

      .addCase(getProjectsList.rejected, (state, action) => {
        const isSelect = action?.meta?.arg?.forSelect;
        const page = action?.meta?.arg?.pageNo || 1;
        if (!isSelect) state.loader = false;
        if (isSelect) {
          // remove failed page from in-flight list so it can be retried
          state.selectFetchingPages = state.selectFetchingPages.filter(
            (p) => p !== page,
          );
        }
      });

    //project AllMembers list reducers
    builder
      .addCase(getProjectsMembersList.pending, (state) => {
        state.loader = true; // always show loader
      })

      .addCase(getProjectsMembersList.fulfilled, (state, action) => {
        const members = action.payload.data.map((m) => ({
          ...m,
          starred: m?.starred ?? m?.isStarred ?? false,
        }));

        const pageNo = action.meta?.arg?.pageNo || 1;
        const shouldAppend = action.meta?.arg?.append && pageNo > 1;

        if (shouldAppend) {
          state.membersList = [...state.membersList, ...members];
        } else {
          state.membersList = members;
        }

        state.total = action.payload.total;
        state.sortByColumn = action.payload.sortByColumn;
        state.orderBy = action.payload.orderBy;
        state.loader = false;
      })

      .addCase(getProjectsMembersList.rejected, (state) => {
        state.loader = false;
      })

      //default icons reducers
      .addCase(getDefaultIcons.fulfilled, (state, action) => {
        state.defaultIcons = action.payload.data;
      })

      //create project reducers
      .addCase(createProject.fulfilled, (state, action) => {
        console.log(action.payload.data);

        state.loading = false;
      })

      //get project data reducers
      .addCase(getProjectData.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getProjectData.fulfilled, (state, action) => {
        state.projectData = action.payload.data;
        state.loading = false;
      })
      .addCase(getProjectData.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.shouldFetchProjects = true;
      });
  },
});

export default projectSlice.reducer;
export const {
  setLoading,
  setLoader,
  setPageNo,
  setPageSize,
  setSort,
  setParams,
  clearProjectData,
  clearSelectCache,
} = projectSlice.actions;
